import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  withMultiSelectCombobox,
  useFluentV1Styles,
} from '@bingads-webui-campaign-react/multi-selector';
import { useDebouncedState } from '@bingads-webui-campaign-react/common-generic-hooks';
import {
  FluentProvider,
  webLightTheme,
  makeStyles,
  mergeClasses,
  shorthands,
  useId,
  Combobox,
  Option,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { InlineLoading, LoadingOverlay } from '@bingads-webui-campaign-react/loading';
import { RequestBrandButton } from './request-brand-button';
import { MaxSelectionAlertForBrands, MoreOptionsInlineAlertForBrands, SystemErrorAlertForBrands } from '../alerts';
import { useBrandsData } from './brands-data';
import { MT_ALLOWED_MIN_SEARCH_FILTER_LENGTH } from '../constants';

const MultiSelectCombobox = withMultiSelectCombobox(Combobox);
const searchStartDelayMs = 500;

const useMultiSelectorStyles = makeStyles({
  listbox: {
    maxHeight: '500px',
  },
});

const useStyles = makeStyles({
  ExistingBrandsPicker: {
    width: '100%',
  },
  BrandItem: {
    width: '545px',
    overflowWrap: 'break-word',
    paddingLeft: '8px',
    '&:hover': {
      backgroundColor: '#f4f4f4',
      cursor: 'pointer',
    },
  },
  BrandItemLink: {
    color: '#0078d4',
    ...shorthands.textDecoration('underline'),
  },
  TypeInContent: {
    ...shorthands.margin('12px'),
    fontSize: '14px',
    lineHeight: '20px',
  },
  NonOptionContent: {
    ...shorthands.margin('12px'),
    '& .brand-empty-header': {
      fontSize: '14px',
      lineHeight: '20px',
    },
    '& .brand-empty-description': {
      fontSize: '12px',
      lineHeight: '20px',
    },
  },
});

export const ExistingBrandsPicker = ({ onRequestBrand }) => {
  const i18n = useContext(I18nContext);
  const classes = useStyles();

  const comboboxId = useId('combobox');
  const styles = useMultiSelectorStyles();
  const fluentV1ComboboxStyles = useFluentV1Styles();

  const {
    isLoadingOptions,
    isFirstOptionsLoad,
    // isNoOptionsAfterFirstLoad, // No need to handle this, since there is an create brand button
    isMoreOptionsThanLimit,
    options,
    optionsLoadError,
    lastLoadedSearchFilter,
    optionsDisplayLimit,
    selectedOptionsLimit,

    isLoadingSelectedOptions,
    isSelectedOptionsLimitReached,
    selectedOptionsLoadError,
    selectedOptions,
    setSelectedOptions,

    setSearchFilter: setSearchFilterFromContext,
  } = useBrandsData();

  const isAnyLoading = isLoadingOptions || isLoadingSelectedOptions;
  const hasLoadErrors = optionsLoadError || selectedOptionsLoadError;
  const showInitialLoading = (isFirstOptionsLoad && !hasLoadErrors) || isLoadingSelectedOptions;
  const canUserStartOperating = !showInitialLoading && !selectedOptionsLoadError;
  const selectedOptionKeys = useMemo(() => _.pluck(selectedOptions, 'key'), [selectedOptions]);
  const selectedOptionsSet = useMemo(() => new Set(selectedOptionKeys), [selectedOptionKeys]);
  const shouldDisableSelection = useCallback(option => isSelectedOptionsLimitReached && !selectedOptionsSet.has(option.key), [isSelectedOptionsLimitReached, selectedOptionsSet]);

  const [isOpen, setIsOpen] = useState(false);
  const onOpenChange = useCallback((event, data) => {
    setIsOpen(data.open);
  }, []);

  const onOptionSelect = useCallback((event, data) => {
    setSelectedOptions(data.resolvedSelectedOptions);
  }, [setSelectedOptions]);

  const [debouncedSearchFilter, setSearchFilter, searchFilter] = useDebouncedState('', searchStartDelayMs);
  useEffect(() => {
    setSearchFilterFromContext(debouncedSearchFilter);
  }, [debouncedSearchFilter, setSearchFilterFromContext]);
  const onChange = useCallback((event) => {
    const { value: rawValue } = event.target;
    setSearchFilter(rawValue);
  }, [setSearchFilter]);

  const isSearchFilterEmpty = !lastLoadedSearchFilter || lastLoadedSearchFilter.length < MT_ALLOWED_MIN_SEARCH_FILTER_LENGTH;

  return (
    <FluentProvider theme={webLightTheme}>
      {isAnyLoading && isFirstOptionsLoad && <InlineLoading text={i18n.getString(_TL_('Loading...'))} />}
      {!isAnyLoading && hasLoadErrors && <SystemErrorAlertForBrands />}
      {!showInitialLoading && isSelectedOptionsLimitReached && <MaxSelectionAlertForBrands selectedOptionsLimit={selectedOptionsLimit} />}
      {canUserStartOperating && (
        <MultiSelectCombobox
          id={comboboxId}
          placeholder={i18n.getString(_TL_('Add brands to your list'))}
          listbox={{ className: styles.listbox }}
          root={{ className: mergeClasses(fluentV1ComboboxStyles.root, classes.ExistingBrandsPicker, 'existing-brands-picker') }}
          disabled={!canUserStartOperating}
          onOptionSelect={onOptionSelect}
          value={searchFilter}
          onChange={onChange}
          options={options}
          selectedOptions={selectedOptions}
          open={isOpen}
          onOpenChange={onOpenChange}
        >
          <LoadingOverlay className={classes.ExistingBrandsPickerContent} isVisible={isLoadingOptions} flexDirection="column">
            {!optionsLoadError &&
              <>
                {isSearchFilterEmpty && _.isEmpty(options) && (
                  <div className={classNames(classes.TypeInContent, 'brand-type')}>
                    {i18n.getString(_TL_('Start typing to search for brands'))}
                  </div>
                )}
                {!isSearchFilterEmpty && _.isEmpty(options) && (
                  <div className={classNames(classes.NonOptionContent, 'brand-empty')}>
                    <div className="brand-empty-header">
                      {i18n.getString(_TL_('No brand found'))}
                    </div>
                    <div className="brand-empty-description">
                      {i18n.getString(_TL_('You can request a brand to add to your shared library.'))}
                    </div>
                    <RequestBrandButton onClick={onRequestBrand} />
                  </div>
                )}
                {!_.isEmpty(options) && options.map(brand => (
                  <Option key={brand.key} value={brand.key} text={brand.name} disabled={isLoadingOptions || shouldDisableSelection(brand)}>
                    <div className={classNames(classes.BrandItem, 'brand-item')}>
                      <div className={classNames(classes.BrandItemName, 'brand-item-name')}>
                        {brand.name} {brand.isUnderReview && `(${i18n.getString(_TL_('requested'))})`} {`(${brand.key})`}
                      </div>
                      <div className={classNames(classes.BrandItemLink, 'brand-item-link')}>
                        {brand.link}
                      </div>
                    </div>
                  </Option>
                ))}
                {isMoreOptionsThanLimit && <MoreOptionsInlineAlertForBrands optionsLimit={optionsDisplayLimit} />}
              </>
            }
            {optionsLoadError &&
              <div className={classNames(classes.NonOptionContent, 'brand-load-error')}>
                {i18n.getString(_TL_('We cannot load the brands at this time. Please try again later.'))}
              </div>
            }
          </LoadingOverlay>
        </MultiSelectCombobox>
      )}
    </FluentProvider>
  );
};

ExistingBrandsPicker.propTypes = {
  onRequestBrand: PropTypes.func.isRequired,
};
